import { APPEAL_RESOLUTIONS, VIOLATION_REVIEW_NOTE_MAX_LENGTH } from '@aysar/contracts';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { AccountRefCell } from '@/shared/components/account/AccountRefCell/AccountRefCell';
import { ErrorState } from '@/shared/components/feedback/ErrorState/ErrorState';
import { Skeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { ChoiceCards } from '@/shared/components/selection/ChoiceCards/ChoiceCards';
import { Badge } from '@/shared/components/ui/Badge/Badge';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { RelativeTime } from '@/shared/components/ui/RelativeTime/RelativeTime';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';
import { useDirection } from '@/shared/hooks/useDirection/useDirection';
import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { accountPath } from '@/shared/utils/account-path/account-path.util';
import { formatNumber } from '@/shared/utils/format-number/format-number.util';

import { ViolationBehaviorHistory } from '../ViolationBehaviorHistory';
import { ViolationEvidence } from '../ViolationEvidence';
import { DecisionRefusal } from '../DecisionRefusal/DecisionRefusal';

import { useViolationReviewModalLogic } from './useViolationReviewModalLogic';

/**
 * One violation: the message, the classifier's verdict, and what happens next.
 *
 * It **reads** for `violations.review` and **decides** for `violations.manage`,
 * in one dialog — see `violations-modals.config.tsx` for why that is one control
 * and not two. Everything on screen comes from the dialog's own query, so it
 * cannot be showing a case the queue's poll has already moved past.
 *
 * **The message body is here and nowhere else.** Chat is member-to-member by
 * construction (N13), so this single-row read is the only place in the product a
 * moderator can see the thing they are judging — and it is confined to a
 * single-row read under `violations.review` for exactly that reason.
 *
 * **Nothing on this dialog enforces.** Confirming opens a second dialog on top,
 * chosen from the row: the consequence deserves its own deliberate act, and the
 * two shapes of that act are structurally different requests.
 */
export function ViolationReviewModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('violations');
  const { can } = useHasPermission();
  const { locale } = useDirection();
  const {
    violation,
    isLoading,
    loadError,
    reload,
    canDecide,
    isQueued,
    isAppealed,
    isDecided,
    openEnforce,
    note,
    setNote,
    resolution,
    setResolution,
    canDismiss,
    canDecideAppeal,
    isSaving,
    dismiss,
    decideAppeal,
    error,
    isConflict,
    isGone,
  } = useViolationReviewModalLogic(modalData, onCancel);

  const confidence =
    violation === null || violation.aiConfidence === null
      ? null
      : formatNumber(violation.aiConfidence, locale, {
          style: 'percent',
          maximumFractionDigits: 0,
        });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('review.title')}</DialogTitle>
          <DialogDescription>{t('review.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <div className="flex flex-col gap-md" aria-label={t('review.loading')}>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : loadError !== null ? (
            <ErrorState error={loadError} title={t('review.loadFailed')} onRetry={reload} />
          ) : violation === null ? null : (
            <div className="flex flex-col gap-lg">
              <div className="grid gap-md sm:grid-cols-2">
                <div className="flex flex-col gap-2xs">
                  <span className="text-caption text-muted-foreground">{t('review.offender')}</span>
                  <AccountRefCell
                    account={violation.offender}
                    href={accountPath(violation.offender, can)}
                  />
                </div>

                <div className="flex flex-col gap-2xs">
                  {/* The number that separates a first mistake from a pattern.
                      Computed server-side over the whole record, so it is the one
                      fact a moderator cannot reconstruct from this screen. */}
                  <span className="text-caption text-muted-foreground">
                    <RelativeTime value={violation.detectedAt} />
                  </span>
                  <span className="text-body">
                    {violation.standingViolations <= 1
                      ? t('review.firstOffence')
                      : t('review.standing', { count: violation.standingViolations })}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-sm">
                {violation.violationTypeKey === null ? (
                  <Badge tone="neutral">{t('queue.unclassified')}</Badge>
                ) : (
                  <>
                    <Badge tone="neutral">
                      {violation.violationTypeName ?? violation.violationTypeKey}
                    </Badge>
                    {violation.severity === null ? null : (
                      <Badge tone="neutral">{t(`severity.${violation.severity}`)}</Badge>
                    )}
                  </>
                )}
                {violation.queueReason === null ? null : (
                  <span className="text-caption text-muted-foreground">
                    {t(`queueReasonHint.${violation.queueReason}`)}
                  </span>
                )}
              </div>

              <ViolationEvidence subject={violation.subject} />

              {violation.subject.kind === 'behavior' ? (
                <ViolationBehaviorHistory
                  history={violation.subject.history}
                  currentRule={violation.subject.rule}
                />
              ) : null}

              <section className="flex flex-col gap-2xs rounded-md bg-muted p-md">
                <h3 className="text-caption font-medium text-foreground">
                  {t('review.aiVerdict')}
                </h3>
                <span className="text-body">
                  {/* `null` is NOT zero: an AI refusal has no score, and `0%`
                      would read as "the model was sure it was fine". */}
                  {confidence === null
                    ? t('review.aiNoConfidence')
                    : t('review.aiConfidence', { percent: confidence })}
                </span>
                <span className="text-caption text-muted-foreground">
                  {violation.aiReason ?? t('review.noAiReason')}
                </span>
              </section>

              {isDecided ? (
                <section className="flex flex-col gap-xs rounded-md bg-muted p-md">
                  <h3 className="text-caption font-medium text-foreground">
                    {t('review.decided.title')}
                  </h3>
                  <span className="text-body">
                    {t(`status.${violation.status}`)}
                    {violation.actionTaken === null
                      ? null
                      : ` · ${t(`action.${violation.actionTaken}`)}`}
                  </span>
                  <span className="text-caption text-muted-foreground">
                    <RelativeTime value={violation.reviewedAt ?? violation.enforcedAt} />
                  </span>
                  <span className="text-caption text-muted-foreground">
                    {violation.reviewNote ?? t('review.decided.noNote')}
                  </span>
                </section>
              ) : null}

              {isAppealed ? (
                <section className="flex flex-col gap-xs">
                  <h3 className="text-caption font-medium text-foreground">
                    {t('review.appeal.title')}
                  </h3>
                  <span className="text-caption text-muted-foreground">
                    <RelativeTime value={violation.appealedAt} />
                  </span>
                  <p className="whitespace-pre-wrap break-words text-body">
                    {violation.appealText}
                  </p>
                </section>
              ) : null}

              {canDecide && isAppealed ? (
                <ChoiceCards
                  label={t('review.appeal.decide')}
                  items={APPEAL_RESOLUTIONS.map((value) => ({
                    value,
                    label: t(`appealResolution.${value}`),
                    description: t(`appealResolutionHint.${value}`),
                  }))}
                  value={resolution}
                  onValueChange={(value) => {
                    // Narrowed by construction: the cards are built from
                    // `APPEAL_RESOLUTIONS`, so nothing else can come back.
                    setResolution(value as (typeof APPEAL_RESOLUTIONS)[number]);
                  }}
                />
              ) : null}

              {canDecide && (isQueued || isAppealed) ? (
                <div className="flex flex-col gap-xs">
                  <Label htmlFor="violation-review-note">
                    {t(isAppealed ? 'review.appeal.note' : 'review.dismiss.note')}
                  </Label>
                  <Textarea
                    id="violation-review-note"
                    aria-describedby="violation-review-note-hint"
                    value={note}
                    maxLength={VIOLATION_REVIEW_NOTE_MAX_LENGTH}
                    onChange={(event) => {
                      setNote(event.target.value);
                    }}
                  />
                  <p id="violation-review-note-hint" className="text-caption text-muted-foreground">
                    {t(isAppealed ? 'review.appeal.noteHint' : 'review.dismiss.noteHint')}
                  </p>
                </div>
              ) : null}

              {canDecide ? null : (
                <p className="flex items-start gap-sm text-body text-muted-foreground">
                  <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{t('review.readOnly')}</span>
                </p>
              )}

              <DecisionRefusal
                message={error}
                isConflict={isConflict}
                isGone={isGone}
                onReload={reload}
                onClose={onCancel}
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('review.close')}
          </Button>

          {canDismiss ? (
            <Button
              variant="secondary"
              isLoading={isSaving}
              onClick={() => {
                void dismiss();
              }}
            >
              {t('review.dismiss.submit')}
            </Button>
          ) : null}

          {canDecide && isQueued ? (
            // Opens the confirmation rather than enforcing: a consequence is its
            // own deliberate act, and which dialog opens is decided by the row.
            <Button onClick={openEnforce}>{t('review.enforce.open')}</Button>
          ) : null}

          {canDecide && isAppealed ? (
            <Button
              isLoading={isSaving}
              disabled={!canDecideAppeal}
              onClick={() => {
                void decideAppeal();
              }}
            >
              {t('review.appeal.submit')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
